import { useCallback, useEffect, useMemo, useState } from 'react';
import { track } from './analytics';

export type CartLine = { productId: string; variantId: 'default'; quantity: number };

const CART_KEY = 'spark-drawer-cart-v1';
const DRAWER_KEY = 'spark-drawer-saved-v1';

function readJson<T>(key: string, fallback: T): T {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(key) ?? 'null');
    return parsed === null ? fallback : parsed as T;
  } catch {
    return fallback;
  }
}

function sanitizeCart(value: CartLine[]): CartLine[] {
  if (!Array.isArray(value)) return [];
  const unique = new Map<string, CartLine>();
  for (const item of value.slice(0, 20)) {
    if (!item || typeof item.productId !== 'string') continue;
    unique.set(item.productId, { productId: item.productId, variantId: 'default', quantity: Math.min(20, Math.max(1, Number.isInteger(item.quantity) ? item.quantity : 1)) });
  }
  return [...unique.values()];
}

export function useCommerceState() {
  const [cart, setCart] = useState<CartLine[]>(() => sanitizeCart(readJson<CartLine[]>(CART_KEY, [])));
  const [saved, setSaved] = useState<string[]>(() => {
    const value = readJson<unknown[]>(DRAWER_KEY, []);
    return [...new Set(value.filter((item): item is string => typeof item === 'string'))].slice(0, 100);
  });

  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem(DRAWER_KEY, JSON.stringify(saved)); }, [saved]);

  const addToCart = useCallback((productId: string) => {
    track('add_to_cart', { productId, quantity: 1 });
    setCart((current) => {
      const found = current.find((line) => line.productId === productId);
      return found ? current.map((line) => line.productId === productId ? { ...line, quantity: Math.min(20, line.quantity + 1) } : line) : [...current, { productId, variantId: 'default', quantity: 1 }];
    });
  }, []);
  const changeQuantity = useCallback((productId: string, quantity: number) => setCart((current) => quantity <= 0 ? current.filter((line) => line.productId !== productId) : current.map((line) => line.productId === productId ? { ...line, quantity: Math.min(20, quantity) } : line)), []);
  const removeFromCart = useCallback((productId: string) => setCart((current) => current.filter((line) => line.productId !== productId)), []);
  const clearCart = useCallback(() => setCart([]), []);
  const toggleSaved = useCallback((productId: string) => setSaved((current) => current.includes(productId) ? current.filter((id) => id !== productId) : [...current, productId]), []);

  const cartCount = useMemo(() => cart.reduce((sum, line) => sum + line.quantity, 0), [cart]);
  return { cart, cartCount, saved, addToCart, changeQuantity, removeFromCart, clearCart, toggleSaved };
}
